import './style.scss';

import {
	autoUpdate,
	flip,
	FloatingFocusManager,
	FloatingPortal,
	offset,
	shift,
	size,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
	useTransitionStatus,
} from '@floating-ui/react';
import { type ReactNode, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import WalletSparkline from '@/app/my-wallet/components/WalletSparkline';
import { INSIGHTS_WINDOW_DAYS } from '@/app/my-wallet/utils/constants';
import { buildSparklineSeries, daysAgoIso } from '@/app/my-wallet/utils/transactions';
import type { IHeldCoupon } from '@/components/coupon/held-coupons';
import { useHeldCoupons } from '@/components/coupon/use-held-coupons';
import AnimatedNumber from '@/components/reusable/animated-number';
import { Skeleton } from '@/components/reusable/skeleton';
import { pageurl } from '@/constants/pageurl';
import { RightSectionContext } from '@/context/right-section';
import { handleError } from '@/helpers/data';
import { useWalletNavBalance } from '@/hooks/wallet';
import type { IWalletResponse } from '@/store/api/wallet/wallets.api';
import { useGetWalletTransactionHistoryMutation } from '@/store/api/wallet/wallets.api';

import ArrivalBubble from './ArrivalBubble';
import CouponPocket from './CouponPocket';

const POPOVER_REFRESH_TICK_MS = 15_000;
const EXPIRY_WARNING_DAYS = 7;
const BUCKETS_VISIBLE = 3;
const SPARKLINE_WINDOW_DAYS = 7;
const ARRIVAL_BUBBLE_MS = 10_000;

type Bucket = IWalletResponse['creditBuckets'][number];

const formatRelative = (since: number | null): string => {
	if (!since) return 'Just now';
	const secs = Math.max(0, Math.floor((Date.now() - since) / 1000));
	if (secs < 5) return 'Just now';
	if (secs < 60) return `${secs}s ago`;
	const mins = Math.floor(secs / 60);
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	return `${Math.floor(hrs / 24)}d ago`;
};

const daysUntil = (iso: string | null): number | null => {
	if (!iso) return null;
	const t = new Date(iso).getTime();
	if (Number.isNaN(t)) return null;
	const diff = t - Date.now();
	return Math.ceil(diff / (24 * 60 * 60 * 1000));
};

const titleCaseBucket = (raw: string): string => {
	if (!raw) return 'Credits';
	return raw
		.toLowerCase()
		.split('_')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

interface BucketRowProps {
	bucket: Bucket;
}

const BucketRow = ({ bucket }: BucketRowProps) => {
	const days = daysUntil(bucket.expiresAt);
	const showExpiry = days !== null && days >= 0;
	const expiryUrgent = showExpiry && days <= EXPIRY_WARNING_DAYS;
	return (
		<li className='wallet-nav__bucket-row'>
			<span className='wallet-nav__bucket-label'>{titleCaseBucket(bucket.creditType)}</span>
			<span className='wallet-nav__bucket-amount'>{bucket.currentBalance.toLocaleString('en-NG')}</span>
			{showExpiry ? (
				<span
					className={`wallet-nav__bucket-expiry ${expiryUrgent ? 'wallet-nav__bucket-expiry--urgent' : ''}`}
					title={`Expires ${new Date(bucket.expiresAt!).toLocaleDateString('en-NG')}`}
				>
					{days === 0 ? 'Today' : `${days}d`}
				</span>
			) : (
				<span className='wallet-nav__bucket-expiry wallet-nav__bucket-expiry--none'>—</span>
			)}
		</li>
	);
};

interface PopoverPanelProps {
	popoverId: string;
	walletId: string | undefined;
	balance: number;
	available: number;
	pending: number;
	currency: string;
	buckets: Bucket[];
	formattedNaira: string | null;
	balanceDiscrepancy: boolean;
	isLoading: boolean;
	isError: boolean;
	isEmpty: boolean;
	lowBalance: boolean;
	lastUpdatedAt: number | null;
	onRefresh: () => void;
	onClose: () => void;
	/** Broadcast coupons, listed above the balance they'd be used on. */
	coupons?: ReactNode;
}

const PopoverPanel = ({
	popoverId,
	walletId,
	balance,
	available,
	pending,
	currency,
	buckets,
	formattedNaira,
	balanceDiscrepancy,
	isLoading,
	isError,
	isEmpty,
	lowBalance,
	lastUpdatedAt,
	onRefresh,
	onClose,
	coupons,
}: PopoverPanelProps) => {
	const navigate = useNavigate();
	const [, forceTick] = useState(0);
	const [getTxHistory, txResult] = useGetWalletTransactionHistoryMutation({
		fixedCacheKey: 'wallet-nav-sparkline',
	});
	const sparklineFetchedRef = useRef(false);

	useEffect(() => {
		const id = setInterval(() => forceTick((n) => n + 1), POPOVER_REFRESH_TICK_MS);
		return () => clearInterval(id);
	}, []);

	useEffect(() => {
		if (!walletId || sparklineFetchedRef.current) return;
		sparklineFetchedRef.current = true;
		getTxHistory({
			walletId,
			fromDate: daysAgoIso(SPARKLINE_WINDOW_DAYS),
			pageSize: 50,
			pageNumber: 1,
			includeInternalTransactions: false,
		})
			.unwrap()
			.catch(handleError);
	}, [walletId, getTxHistory]);

	const sparklineSeries = useMemo(() => buildSparklineSeries(txResult.data?.items ?? []), [txResult.data]);
	const sparklineLoading = txResult.isLoading;

	const goToWallet = useCallback(
		(action?: 'topup') => {
			onClose();
			navigate(action === 'topup' ? `${pageurl.MYWALLET}?action=topup` : pageurl.MYWALLET);
		},
		[onClose, navigate]
	);

	const visibleBuckets = useMemo(() => {
		const sorted = [...buckets].sort((a, b) => b.currentBalance - a.currentBalance);
		return sorted.slice(0, BUCKETS_VISIBLE);
	}, [buckets]);
	const hiddenBucketCount = Math.max(0, buckets.length - visibleBuckets.length);

	const isFetching = txResult.isLoading || isLoading;
	void INSIGHTS_WINDOW_DAYS; // imported for future per-window tuning; suppress unused warning

	return (
		<div
			id={popoverId}
			role='dialog'
			aria-label='Wallet details'
			className={`wallet-nav__panel ${isError ? 'wallet-nav__panel--error' : ''}`}
		>
			{coupons}
			<header className='wallet-nav__panel-header'>
				<span className='wallet-nav__eyebrow'>Wallet Balance</span>
				<div className='wallet-nav__panel-meta'>
					<span className='wallet-nav__updated'>{isError ? 'Refresh failed' : `Updated ${formatRelative(lastUpdatedAt)}`}</span>
					<button
						type='button'
						className={`wallet-nav__refresh ${isFetching ? 'is-spinning' : ''}`}
						onClick={onRefresh}
						aria-label='Refresh wallet balance'
						disabled={isFetching}
					>
						<svg
							width='14'
							height='14'
							viewBox='0 0 24 24'
							fill='none'
							aria-hidden='true'
						>
							<path
								d='M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3M21 3v6h-6M3 21v-6h6'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</button>
				</div>
			</header>

			{balanceDiscrepancy ? (
				<div
					className='wallet-nav__banner'
					role='note'
				>
					Balance under review. Final available credit may differ.
				</div>
			) : null}

			<div className='wallet-nav__hero'>
				{isLoading ? (
					<Skeleton
						width={160}
						height={36}
						animation='shimmer'
					/>
				) : isError ? (
					<span className='wallet-nav__hero-amount wallet-nav__hero-amount--muted'>—</span>
				) : (
					<h2 className='wallet-nav__hero-amount'>
						<AnimatedNumber
							value={balance}
							duration={600}
						/>
					</h2>
				)}
				<span className='wallet-nav__hero-sub'>
					{currency} · {formattedNaira ?? '—'}
				</span>
			</div>

			<div className='wallet-nav__split'>
				<div className='wallet-nav__split-row'>
					<span
						className='wallet-nav__dot wallet-nav__dot--success'
						aria-hidden='true'
					/>
					<span className='wallet-nav__split-label'>Available</span>
					<span className='wallet-nav__split-amount'>{available.toLocaleString('en-NG')}</span>
				</div>
				<div className='wallet-nav__split-row'>
					<span
						className='wallet-nav__dot wallet-nav__dot--warning'
						aria-hidden='true'
					/>
					<span className='wallet-nav__split-label'>Pending</span>
					<span className='wallet-nav__split-amount'>{pending.toLocaleString('en-NG')}</span>
				</div>
			</div>

			{buckets.length > 0 && !isEmpty ? (
				<section
					className='wallet-nav__buckets'
					aria-label='Credit buckets'
				>
					<span className='wallet-nav__eyebrow wallet-nav__eyebrow--small'>Credit Buckets</span>
					<ul className='wallet-nav__bucket-list'>
						{visibleBuckets.map((bucket) => (
							<BucketRow
								key={bucket.id}
								bucket={bucket}
							/>
						))}
					</ul>
					{hiddenBucketCount > 0 ? (
						<button
							type='button'
							className='wallet-nav__more-link'
							onClick={() => goToWallet()}
						>
							+ {hiddenBucketCount} more {hiddenBucketCount === 1 ? 'bucket' : 'buckets'}
						</button>
					) : null}
				</section>
			) : null}

			<section
				className='wallet-nav__spark'
				aria-label='Seven-day spend trend'
			>
				{sparklineLoading ? (
					<Skeleton
						width='100%'
						height={40}
						animation='shimmer'
					/>
				) : sparklineSeries.length >= 2 ? (
					<>
						<WalletSparkline
							series={sparklineSeries}
							width={272}
							height={40}
							accent={lowBalance ? 'negative' : 'positive'}
						/>
						<span className='wallet-nav__spark-label'>7-day balance trend</span>
					</>
				) : (
					<span className='wallet-nav__spark-empty'>No activity in the last {SPARKLINE_WINDOW_DAYS} days.</span>
				)}
			</section>

			<footer className='wallet-nav__panel-footer'>
				{balanceDiscrepancy ? (
					<button
						type='button'
						className='wallet-nav__cta wallet-nav__cta--primary'
						onClick={() => goToWallet()}
					>
						Contact Support
					</button>
				) : (
					<button
						type='button'
						className='wallet-nav__cta wallet-nav__cta--primary'
						onClick={() => goToWallet('topup')}
					>
						Add Credit
					</button>
				)}
				<button
					type='button'
					className='wallet-nav__cta wallet-nav__cta--link'
					onClick={() => goToWallet()}
				>
					View Wallet →
				</button>
			</footer>
		</div>
	);
};

interface PillProps {
	formattedNaira: string | null;
	formattedCredits: string;
	status: 'walletless' | 'loading' | 'error' | 'empty' | 'low' | 'healthy';
	lowBalance: boolean;
	isEmpty: boolean;
	isOpen: boolean;
	popoverId: string;
	a11yLabel: string;
	balance: number;
	previousBalance: number;
	onTopUp: (e: React.MouseEvent) => void;
	innerProps: Record<string, unknown>;
	referenceRef: (node: HTMLElement | null) => void;
	/** Unclaimed broadcast coupons waiting in the popover. */
	couponCount: number;
	couponSoon: boolean;
	/** A coupon just arrived — pulse the badge while the arrival bubble shows. */
	couponNew: boolean;
}

const Pill = ({
	formattedNaira,
	formattedCredits,
	status,
	lowBalance,
	isEmpty,
	isOpen,
	popoverId,
	a11yLabel,
	balance,
	previousBalance,
	onTopUp,
	innerProps,
	referenceRef,
	couponCount,
	couponSoon,
	couponNew,
}: PillProps) => {
	const [tick, setTick] = useState(false);
	useEffect(() => {
		if (balance === previousBalance) return;
		setTick(true);
		const t = setTimeout(() => setTick(false), 240);
		return () => clearTimeout(t);
	}, [balance, previousBalance]);

	return (
		<button
			ref={referenceRef}
			type='button'
			className={`wallet-nav__pill wallet-nav__pill--${status} ${isOpen ? 'is-open' : ''}`}
			aria-label={a11yLabel}
			aria-haspopup='dialog'
			aria-expanded={isOpen}
			aria-controls={popoverId}
			{...innerProps}
		>
			<span
				className={`wallet-nav__rail ${tick ? 'wallet-nav__rail--ticking' : ''}`}
				aria-hidden='true'
			/>
			{status === 'loading' ? (
				<span className='wallet-nav__pill-inner'>
					<Skeleton
						width={32}
						height={10}
						animation='shimmer'
					/>
					<Skeleton
						width={64}
						height={12}
						animation='shimmer'
					/>
				</span>
			) : status === 'error' ? (
				<span className='wallet-nav__pill-inner'>
					<span className='wallet-nav__pill-label'>BAL</span>
					<span className='wallet-nav__pill-amount wallet-nav__pill-amount--muted'>—</span>
					<span
						className='wallet-nav__pill-warn'
						aria-hidden='true'
						title="Couldn't refresh"
					>
						⚠
					</span>
				</span>
			) : isEmpty ? (
				<span className='wallet-nav__pill-inner'>
					<span className='wallet-nav__pill-empty'>Wallet empty</span>
					<span
						className='wallet-nav__pill-dot'
						aria-hidden='true'
					>
						·
					</span>
					<span
						role='link'
						className='wallet-nav__pill-cta'
						onClick={onTopUp}
					>
						Add credit
					</span>
				</span>
			) : (
				<span className='wallet-nav__pill-inner'>
					<span className='wallet-nav__pill-label'>BAL</span>
					<span className='wallet-nav__pill-amount'>{formattedCredits ?? formattedNaira}</span>
					{lowBalance ? (
						<>
							<span
								className='wallet-nav__pill-dot'
								aria-hidden='true'
							>
								·
							</span>
							<span
								role='link'
								className='wallet-nav__pill-cta'
								onClick={onTopUp}
							>
								Top up
							</span>
						</>
					) : null}
				</span>
			)}
			{couponCount > 0 ? (
				<span
					className={`coupon-pocket-badge${couponSoon ? ' coupon-pocket-badge--soon' : ''}${couponNew ? ' coupon-pocket-badge--new' : ''}`}
					aria-hidden='true'
				>
					{couponCount}
				</span>
			) : null}
			<svg
				className='wallet-nav__chev'
				width='10'
				height='10'
				viewBox='0 0 24 24'
				aria-hidden='true'
			>
				<path
					d='M6 9l6 6 6-6'
					stroke='currentColor'
					strokeWidth='2.4'
					fill='none'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
			</svg>
		</button>
	);
};

const WalletNavWidget = () => {
	const wallet = useWalletNavBalance();
	const navigate = useNavigate();
	const popoverId = useId();
	// Read the context directly: the panel provider is mounted in the app shell, but this widget
	// must still render (without "Use now") where it isn't, e.g. in tests.
	const rightPanel = useContext(RightSectionContext);
	const coupons = useHeldCoupons({ userId: wallet.userId, walletId: wallet.walletId });

	// The arrival bubble: shown for each new coupon (unless the popover is already open, where the
	// coupon is in plain view), hidden after a few seconds unless the pointer or focus is on it.
	const [bubble, setBubble] = useState<IHeldCoupon | null>(null);
	const [bubbleEngaged, setBubbleEngaged] = useState(false);

	const [open, setOpen] = useState(false);
	const previousBalanceRef = useRef(wallet.balance);
	const openRef = useRef(open);
	openRef.current = open;

	const arrivalSeq = coupons.arrival?.seq;
	useEffect(() => {
		if (!coupons.arrival || openRef.current) return;
		setBubble(coupons.arrival.coupon);
		setBubbleEngaged(false);
		// Keyed on the sequence, not the object: a repeat of the same coupon is a new arrival.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [arrivalSeq]);

	useEffect(() => {
		if (!bubble || bubbleEngaged) return;
		const timer = window.setTimeout(() => setBubble(null), ARRIVAL_BUBBLE_MS);
		return () => window.clearTimeout(timer);
	}, [bubble, bubbleEngaged]);

	useEffect(() => {
		if (open) setBubble(null);
	}, [open]);

	useEffect(() => {
		previousBalanceRef.current = wallet.balance;
	}, [wallet.balance]);

	const { refs, floatingStyles, context } = useFloating({
		open,
		onOpenChange: setOpen,
		placement: 'bottom-end',
		strategy: 'fixed',
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(8),
			flip({ padding: 8, fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] }),
			shift({ padding: 8 }),
			size({
				padding: 8,
				apply({ availableWidth, elements }) {
					const max = Math.min(availableWidth - 16, 360);
					elements.floating.style.maxWidth = `${Math.max(280, max)}px`;
				},
			}),
		],
	});

	const click = useClick(context, { event: 'mousedown' });
	const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true });
	const role = useRole(context, { role: 'dialog' });
	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
	const { isMounted, status } = useTransitionStatus(context, { duration: { open: 160, close: 100 } });

	useEffect(() => {
		if (!open) return;
		wallet.refetch();
	}, [open, wallet.refetch]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleTopUp = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			setOpen(false);
			navigate(`${pageurl.MYWALLET}?action=topup`);
		},
		[navigate]
	);

	const openTopUpWithCoupon = useCallback(
		(coupon: IHeldCoupon) => {
			setOpen(false);
			if (!rightPanel) {
				navigate(`${pageurl.MYWALLET}?action=topup`);
				return;
			}
			rightPanel.callSection({
				component: 'wallet-topup',
				action: 'create',
				title: 'Top up wallet',
				data: { couponCode: coupon.code, walletId: wallet.walletId },
			});
		},
		[rightPanel, navigate, wallet.walletId]
	);

	const a11yLabel = useMemo(() => {
		const couponNote = coupons.waiting
			? ` ${coupons.waiting} coupon${coupons.waiting === 1 ? '' : 's'} waiting${coupons.urgent ? ', one expires within a day' : ''}.`
			: '';
		if (wallet.status === 'walletless') return 'No wallet';
		if (wallet.status === 'loading') return `Loading wallet balance.${couponNote}`;
		if (wallet.status === 'error') return `Wallet balance unavailable. Click to retry.${couponNote}`;
		if (wallet.status === 'empty') return `Wallet empty. Click to add credit.${couponNote}`;
		const detail = `${wallet.available.toLocaleString('en-NG')} available, ${wallet.pending.toLocaleString('en-NG')} pending`;
		return `Wallet balance: ${wallet.formattedNaira ?? wallet.formattedCredits}. ${detail}.${couponNote} Click for details.`;
	}, [wallet, coupons.waiting, coupons.urgent]);

	if (wallet.status === 'walletless') return null;

	return (
		<div className='wallet-nav'>
			<Pill
				formattedNaira={wallet.formattedNaira}
				formattedCredits={wallet.formattedCredits}
				status={wallet.status}
				lowBalance={wallet.lowBalance}
				isEmpty={wallet.isEmpty}
				isOpen={open}
				popoverId={popoverId}
				a11yLabel={a11yLabel}
				balance={wallet.balance}
				previousBalance={previousBalanceRef.current}
				onTopUp={handleTopUp}
				innerProps={getReferenceProps()}
				referenceRef={refs.setReference}
				couponCount={coupons.waiting}
				couponSoon={coupons.urgent}
				couponNew={!!bubble}
			/>
			{bubble && !open ? (
				<ArrivalBubble
					coupon={bubble}
					waiting={coupons.waiting}
					now={coupons.now}
					onView={() => {
						setBubble(null);
						setOpen(true);
					}}
					onClose={() => setBubble(null)}
					onEngage={setBubbleEngaged}
				/>
			) : null}
			<span
				className='wallet-nav__sr'
				role='status'
				aria-live='polite'
			>
				{coupons.announcement}
			</span>
			<span
				className='wallet-nav__sr'
				aria-live='polite'
				aria-atomic='true'
			>
				{wallet.status === 'healthy' || wallet.status === 'low'
					? `Wallet balance ${wallet.formattedNaira ?? wallet.formattedCredits}`
					: ''}
			</span>
			{isMounted ? (
				<FloatingPortal>
					<FloatingFocusManager
						context={context}
						modal={false}
						initialFocus={-1}
						returnFocus
					>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							data-status={status}
							className='wallet-nav__floater'
							{...getFloatingProps()}
						>
							<PopoverPanel
								popoverId={popoverId}
								walletId={wallet.walletId}
								balance={wallet.balance}
								available={wallet.available}
								pending={wallet.pending}
								currency={wallet.currency}
								buckets={wallet.buckets}
								formattedNaira={wallet.formattedNaira}
								balanceDiscrepancy={wallet.balanceDiscrepancy}
								isLoading={wallet.isLoading}
								isError={wallet.isError}
								isEmpty={wallet.isEmpty}
								lowBalance={wallet.lowBalance}
								lastUpdatedAt={wallet.lastUpdatedAt}
								onRefresh={wallet.refetch}
								onClose={() => setOpen(false)}
								coupons={
									<CouponPocket
										held={coupons}
										onUseNow={openTopUpWithCoupon}
									/>
								}
							/>
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			) : null}
		</div>
	);
};

export default WalletNavWidget;
